import { useEffect, useState } from "react";
import axios from "axios";
import { Tree, TreeNode } from "react-organizational-chart";
import Loading from "../../components/Loading";

const EmployeeCard = ({ employee, onClick, level }) => {
  const colorLevels = ["#E3F2FD", "#FCE4EC", "#F1F8E9", "#FFF3E0", "#EDE7F6"];
  const bcolorLevels = ["#90CAF9 ", "#F48FB1 ", "#C5E1A5 ", "#FFCC80 ", "#B39DDB "];
  const bgColor = colorLevels[level % colorLevels.length];
  const bordercolorLevels = bcolorLevels[level % bcolorLevels.length];

  return (
    <div className="flex flex-col items-center cursor-pointer transition-transform hover:scale-105">
      <div
        className="relative w-full max-w-xs rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow"
        style={{ backgroundColor: bgColor }}
      >
        {/* Card Content */}
        <div
          className="relative backdrop-blur-lg border-l-8 border-t-8 rounded-xl p-4 flex"
          style={{ borderColor: bordercolorLevels }}
        >
          {/* Profile Image */}
          <div className="flex justify-center pb-3">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-lg font-semibold text-gray-700 bg-gradient-to-br from-gray-200 to-gray-300 shadow-md">
              {employee.first_name[0]}{employee.last_name[0]}
            </div>
          </div>

          {/* Employee Details */}
          <div className="text-center">
            <div className="font-semibold text-gray-900 text-lg">
              {`${employee.first_name} ${employee.last_name}`}
            </div>
            <div className="text-sm text-gray-600 font-medium">{employee.role}</div>
            <div className="text-xs text-gray-500 italic">{employee.email}</div>
          </div>
        </div>
      </div>

      {/* Expand Button */}
      {employee.subordinates && employee.subordinates.length > 0 && (
        <button
          onClick={onClick}
          className="mt-3 bg-indigo-600 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-indigo-700 shadow-md transition-all"
        >
          {employee.expanded ? "−" : "+"}
        </button>
      )}
    </div>
  );
};




const TeamHierarchy = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/team/get-team-details`, {
          withCredentials: true,
        });
        
        const processTeam = (employees) => {
          return employees.map(emp => ({
            ...emp,
            expanded: false,
            subordinates: emp.subordinates ? processTeam(emp.subordinates) : []
          }));
        };
        
        setTeam(processTeam(response.data.team));
      } catch (err) {
        setError("Failed to load team data.");
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);
  
  const toggleExpand = (employeeId) => {
    const updateExpandedState = (employees) => {
      return employees.map(emp => {
        if (emp._id === employeeId) {
          return { ...emp, expanded: !emp.expanded };
        }
        if (emp.subordinates && emp.subordinates.length > 0) {
          return { ...emp, subordinates: updateExpandedState(emp.subordinates) };
        }
        return emp;
      });
    };
    setTeam(updateExpandedState(team));
  };

  const renderTreeNode = (employee, level = 0) => {
    const isFirstLevel = level === 1; // Check if it's Manager or HR level
    const isTeamLead = employee.role === "TL"; // Check if the employee is a Team Lead
  
    return (
      <div
        key={employee._id}
        className={`${
          isFirstLevel || isTeamLead ? "flex flex-row gap-6 justify-center" : "flex flex-col items-center"
        }`}
      >
        <TreeNode
          label={<EmployeeCard employee={employee} onClick={() => toggleExpand(employee._id)} level={level} />}
        >
          {employee.expanded && (
            <div className={`${isTeamLead ? "flex flex-row gap-4 justify-center" : "flex flex-col items-center gap-4"}`}>
              {employee.subordinates.map((sub) => renderTreeNode(sub, level + 1))}
            </div>
          )}
        </TreeNode>
      </div>
    );
  };
  
  
  return (
    <div className="p-6 bg-gray-100 min-h-screen w-full">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 text-start">Team Hierarchy</h1>
  
      {loading && <Loading/>}
      {error && <p className="text-center text-red-500">{error}</p>}
  
      {!loading && !error && team.length > 0 && (
        <div
          className="w-full overflow-x-auto"
          style={{
            overflowX: "auto",
            scrollbarWidth: "thin", // Firefox
            msOverflowStyle: "auto", // IE/Edge
            whiteSpace: "nowrap",
          }}
        >
          {/* Inner wrapper for centering & allowing left visibility */}
          <div
            className="w-full flex justify-start items-center overflow-x-auto"
            style={{
              minWidth: "max-content", // Ensures content is not squished
              overflowX: "auto",
              scrollbarWidth: "thin",
              msOverflowStyle: "auto",
              whiteSpace: "nowrap",
              paddingLeft: "20px", // Reduce fixed left padding
            }}
          >
            <Tree
              lineWidth={"2px"}
              lineColor={"#ccc"}
              lineBorderRadius={"8px"}
              label={<EmployeeCard employee={team[0]} onClick={() => toggleExpand(team[0]._id)} />}
            >
              {team[0].expanded && (
                <div className="flex flex-row gap-6 justify-start overflow-x-auto">
                  {team[0].subordinates.map((emp) => renderTreeNode(emp, 1))}
                </div>
              )}
            </Tree>
          </div>
        </div>
      )}
    </div>
  );
  
};

export default TeamHierarchy;



